import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../../shared/api/client';

export type ContractPeriodicity =
  | 'WEEKLY'
  | 'BIWEEKLY'
  | 'MONTHLY'
  | 'BIMONTHLY'
  | 'QUARTERLY'
  | 'SEMIANNUAL'
  | 'ANNUAL';

export interface ContractItem {
  productId: string;
  description: string;
  quantity: number;
  unitValue: number;
}

export interface RecurringContract {
  id: string;
  tenantId: string;
  clientId: string;
  startDate: string;
  endDate: string | null;
  periodicity: ContractPeriodicity;
  value: number;
  items: ContractItem[];
  status: 'ACTIVE' | 'SUSPENDED' | 'CANCELLED';
  lastBillingDate: string | null;
  nextBillingDate: string;
  cancellationReason: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface ContractInput {
  clientId: string;
  startDate: string;
  endDate?: string;
  periodicity: ContractPeriodicity;
  value: number;
  items: ContractItem[];
}

interface PaginatedResponse<T> {
  data: T[];
  total: number;
  page: number;
  limit: number;
}

/* RF-015/RF-023: contratos da ficha = RecurringContract por clientId. */
export function useContracts(params?: {
  clientId?: string;
  status?: string;
  page?: number;
  limit?: number;
}): ReturnType<typeof useQuery<PaginatedResponse<RecurringContract>>> {
  return useQuery<PaginatedResponse<RecurringContract>>({
    queryKey: ['crm', 'contracts', params],
    queryFn: async () =>
      apiClient.get<PaginatedResponse<RecurringContract>>('/contracts', {
        params: {
          ...(params?.clientId ? { clientId: params.clientId } : {}),
          ...(params?.status ? { status: params.status } : {}),
          page: String(params?.page ?? 1),
          limit: String(params?.limit ?? 20),
        },
      }),
  });
}

function useInvalidateContracts() {
  const queryClient = useQueryClient();
  return () => {
    queryClient.invalidateQueries({ queryKey: ['crm', 'contracts'] });
  };
}

export function useCreateContract() {
  const invalidate = useInvalidateContracts();
  return useMutation<RecurringContract, unknown, ContractInput, unknown>({
    mutationFn: (input: ContractInput) =>
      apiClient.post<RecurringContract>('/contracts', input),
    onSuccess: () => invalidate(),
  });
}

export function useSuspendContract() {
  const invalidate = useInvalidateContracts();
  return useMutation<RecurringContract, unknown, string, unknown>({
    mutationFn: (id: string) => apiClient.patch<RecurringContract>(`/contracts/${id}/suspend`, {}),
    onSuccess: () => invalidate(),
  });
}

export function useReactivateContract() {
  const invalidate = useInvalidateContracts();
  return useMutation<RecurringContract, unknown, string, unknown>({
    mutationFn: (id: string) =>
      apiClient.patch<RecurringContract>(`/contracts/${id}/reactivate`, {}),
    onSuccess: () => invalidate(),
  });
}

export function useCancelContract() {
  const invalidate = useInvalidateContracts();
  return useMutation<RecurringContract, unknown, { id: string; reason: string }, unknown>({
    mutationFn: ({ id, reason }) =>
      apiClient.post<RecurringContract>(`/contracts/${id}/cancel`, { reason }),
    onSuccess: () => invalidate(),
  });
}
